import React, { useState, useEffect, useMemo } from 'react';
import { Eye, EyeOff, User, Waves } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [error, setError] = useState('');
  const [loginSuccess, setLoginSuccess] = useState(false);
  const { showToast } = useToast();
  
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();

  // Balurghat Swimming Pool slides — using Unsplash images
  const poolImages = useMemo(() => [
    {
      title: 'Main Swimming Pool',
      description: 'Olympic-size pool with crystal clear water',
      imageUrl: 'https://images.unsplash.com/photo-1571902943202-507ec2618e8f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80'
    },
    {
      title: 'Training Facilities',
      description: 'Professional training equipment and areas',
      imageUrl: 'https://images.unsplash.com/photo-1530549387789-4c1017266635?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80'
    },
    {
      title: 'Professional Coaching',
      description: 'Expert instructors for all skill levels',
      imageUrl: 'https://images.unsplash.com/photo-1544717297-fa95b6ee9643?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80'
    },
    {
      title: 'Modern Infrastructure',
      description: 'State-of-the-art swimming facilities',
      imageUrl: 'https://images.unsplash.com/photo-1551698618-1dfe5d97d256?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80'
    }
  ], []);

  // Auto-advance slideshow
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % poolImages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [poolImages.length]);



  // Navigate to dashboard when login is successful
  useEffect(() => {
    if (loginSuccess && isAuthenticated) {
      window.location.href = '/dashboard';
      setLoginSuccess(false);
    }
  }, [loginSuccess, isAuthenticated, navigate]);



  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}api/student-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        const userData = { ...data.data, name: data.data.fullname };
        const token = data.data.token;
        
        localStorage.setItem('token', token);
        showToast(data.message, 'success');
        
        login(userData, token);
        setLoginSuccess(true);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Login failed. Please try again.');
      }
    } catch (err) {
      setError('Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-vh-100 d-flex flex-column bg-light login-page">
      <Header />
      

      
      <div className="flex-grow-1 d-flex align-items-center justify-content-center py-5">
        <div className="container">
          <div className="row rounded overflow-hidden" style={{ minHeight: '600px' }}>
            {/* Left Side - Gradient Slideshow with Text Overlay */}
            <div className="col-lg-6 d-none d-lg-block p-0">
              <div className="h-100 position-relative overflow-hidden">
                <div className="h-100 position-relative">
                  {poolImages.map((image, index) => (
                    <div
                      key={index}
                      className={`position-absolute w-100 h-100 ${
                        index === currentImageIndex ? 'opacity-100' : 'opacity-0'
                      }`}
                      style={{
                        backgroundImage: `url(${image.imageUrl})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundColor: '#4a90e2',
                        transition: 'opacity 1.5s ease-in-out, transform 1.5s ease-in-out',
                        transform: index === currentImageIndex ? 'scale(1)' : 'scale(1.1)'
                      }}
                      onError={(e) => {
                        console.error('Image failed to load:', image.imageUrl);
                        e.target.style.backgroundImage = 'none';
                        e.target.style.backgroundColor = '#4a90e2';
                      }}
                    />
                  ))}
                  
                  {/* Overlay */}
                  <div className="position-absolute w-100 h-100" style={{
                    background: 'linear-gradient(to bottom, rgba(0,0,0,0.2), rgba(0,0,0,0.7))'
                  }} />
                </div>

                {/* Text Content */}
                <div className="h-100 d-flex flex-column justify-content-center align-items-center text-white p-5 position-relative" style={{ zIndex: 5 }}>
                  <div className="text-center">
                    <Waves size={60} className="mb-4 login-float" />
                    
                    <h2 
                      className="fw-bold mb-3 login-slide-in-top" 
                      style={{ textShadow: '0 2px 10px rgba(0,0,0,0.5)' }}
                    >
                      {poolImages[currentImageIndex].title}
                    </h2>
                    
                    <p 
                      className="lead mb-4 login-slide-in-bottom" 
                      style={{ textShadow: '0 1px 5px rgba(0,0,0,0.5)', fontSize: '1.1rem' }}
                    >
                      {poolImages[currentImageIndex].description}
                    </p>
                    
                    <h3 className="fw-bold mb-2 login-fade-in" style={{ textShadow: '0 2px 10px rgba(0,0,0,0.5)' }}>
                      Balurghat Swimming Pool
                    </h3>
                    <p className="login-fade-in" style={{ textShadow: '0 1px 5px rgba(0,0,0,0.5)' }}>
                      Student Portal Login
                    </p>

                    {/* Slide indicators */}
                    <div className="d-flex justify-content-center gap-2 mt-3">
                      {poolImages.map((_, idx) => (
                        <span
                          key={idx}
                          onClick={() => setCurrentImageIndex(idx)}
                          style={{
                            width: idx === currentImageIndex ? '24px' : '8px',
                            height: '8px',
                            borderRadius: '4px',
                            background: idx === currentImageIndex ? '#fff' : 'rgba(255,255,255,0.5)',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            display: 'inline-block'
                          }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side - Login Form */}
            <div className="col-lg-6 col-md-10 col-11 mx-auto bg-white p-5">
              <div className="h-100 d-flex flex-column justify-content-center">
                <div className="text-center mb-4">
                  <User size={40} className="text-primary mb-3" />
                  <h3 className="fw-bold mb-2">Student Login</h3>
                </div>

                <form onSubmit={handleSubmit}>
                  {error && (
                    <div className="alert alert-danger mb-3" role="alert">
                      {error}
                    </div>
                  )}
                  
                  <div className="mb-3">
                    <label htmlFor="email" className="form-label">Email Address</label>
                    <input
                      type="email"
                      className="form-control"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Enter your email"
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label htmlFor="password" className="form-label">Password</label>
                    <div className="input-group">
                      <input
                        type={showPassword ? "text" : "password"}
                        className="form-control"
                        id="password"
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        placeholder="Enter your password"
                        required
                      />
                      <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>

                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="form-check">
                      <input className="form-check-input" type="checkbox" id="remember" />
                      <label className="form-check-label" htmlFor="remember">
                        Remember me
                      </label>
                    </div>
                    {/* Use a button styled as a link instead of <a href="#"> */}
                    <Link 
                      className="btn btn-link p-0 text-primary text-decoration-none"
                      style={{ fontSize: '12px' }}
                      to="/forgot-password"
                    >
                      Forgot Password?
                    </Link>
                  </div>

                  <button
                    type="submit"
                    className="btn btn-primary w-100 mb-3"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Signing in...' : 'SIGN IN'}
                  </button>

                  <div className="text-center">
                    <p className="mb-0">
                      Don't have an account?{' '}
                      <Link to="/register" className="text-primary text-decoration-none">
                        Register here
                      </Link>
                    </p>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default Login;
